import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { FormikValues } from "formik";
import { DraftUUID, StepEndpointSecurities } from "../types";

export interface HolderData {
  currentHolder: number;
  USCitizen: boolean;
  taxId: string;
  holdersMarried: boolean;
  stepData: {
    step: number | string | null;
    subStep: number | null;
    completedSteps: number[] | null;
    reachedSteps: number[] | null;
    lastCompletedSubStep: number;
    numericStepList: number[] | null;
  };
  formValues: FormikValues | null;
  sendedSteps: StepEndpointSecurities[];
  completedForm: boolean;
}

// Sentry validation error tracking
export interface ValidationErrorCount {
  fieldName: string;
  errorMessage: string;
  count: number;
}

interface InitialState extends HolderData {
  language: "en" | "es";
  accountHolders: number;
  initialValidationData: {
    email: string;
    referred: string; // email or name
    completed: boolean;
    taxId: string;
  };
  accountBeneficiaries: boolean;
  beneficiariesCount: number;
  holdersData: HolderData[];
  draftUUID: DraftUUID;
  // Sentry: validation error tracking for pattern detection
  validationErrorCounts: ValidationErrorCount[];
}

const initialState: InitialState = {
  language: "es",
  accountHolders: 1,
  holdersMarried: false,
  initialValidationData: {
    email: "",
    referred: "",
    completed: false,
    taxId: "",
  },
  beneficiariesCount: 2,
  accountBeneficiaries: true,
  holdersData: [],
  // Holder data
  currentHolder: 1,
  USCitizen: false,
  taxId: "",
  stepData: {
    step: null,
    subStep: null,
    completedSteps: null,
    reachedSteps: null,
    lastCompletedSubStep: 0,
    numericStepList: null,
  },
  formValues: {},
  sendedSteps: [],
  completedForm: false,
  draftUUID: "",
  validationErrorCounts: [],
};

const securitiesSlice = createSlice({
  name: "securities",
  initialState,
  reducers: {
    onUpdateSecuritiesData: (state, action: {payload: Partial<InitialState>}) => {
      return { ...state, ...action.payload };
    },
    onResetSecuritiesData: () => {
      return initialState;
    },
    onUpdateInitialValidationData: (state, action: {payload: Partial<InitialState["initialValidationData"]>}) => {
        return { ...state, initialValidationData: { ...state.initialValidationData, ...action.payload } };
    },
    onUpdateStep: (state, action: PayloadAction<number | string | null>) => {
        return {
          ...state,
          stepData: { ...state.stepData, step: action.payload },
        };
      },
      onUpdateSubStep: (state, action: PayloadAction<number | null>) => {
        return {
          ...state,
          stepData: { ...state.stepData, subStep: action.payload },
        };
      },
      onUpdateCompletedStep: (state, action: PayloadAction<number>) => {
        // Create an array with all the steps from 1 to the new completed step
        const newCompletedSteps = Array.from(
          { length: action.payload },
          (_, i) => i + 1
        );
  
        return {
          ...state,
          stepData: {
            ...state.stepData,
            completedSteps: newCompletedSteps,
          },
        };
      },
      onUpdateReachedStep: (state, action: PayloadAction<number>) => {
        // Create an array with all the steps from 1 to the new completed step
        const newReachedSteps = Array.from(
          { length: action.payload },
          (_, i) => i + 1
        );
  
        return {
          ...state,
          stepData: {
            ...state.stepData,
            reachedSteps: newReachedSteps,
          },
        };
      },
      onUpdateLastCompletedSubStep: (state, action: PayloadAction<number>) => {
        return {
          ...state,
          stepData: {
            ...state.stepData,
            lastCompletedSubStep: action.payload,
          },
        };
      },
      onUpdateFormData: (state, action: PayloadAction<FormikValues>) => {
        return {
          ...state,
          formValues: {
            ...state.formValues,
            ...action.payload,
          },
        };
      },
      addSendedStep: (state, action: PayloadAction<StepEndpointSecurities>) => {
        if (!state.sendedSteps)
          return { ...state, sendedSteps: [action.payload] };
        else {
          const filteredState = state.sendedSteps.filter(
            (step) => step["@type"] !== action.payload["@type"]
          );
  
          filteredState.push(action.payload);
          return {
            ...state,
            sendedSteps: filteredState,
          };
        }
      },
      onUpdateCompletedForm: (state, action: PayloadAction<boolean>) => {
        return { ...state, completedForm: action.payload };
      },
      onUpdateAccountBeneficiaries: (state, action: PayloadAction<{count: number, bool: boolean}>) => {
        return { ...state, beneficiariesCount: action.payload.count, accountBeneficiaries: action.payload.bool };  
      },
      onUpdateHolder: (state, action: PayloadAction<number>) => {
        const newHolderData = state.holdersData.find(holder => holder.currentHolder === action.payload);
        const emptyHolderData: HolderData = {
          currentHolder: action.payload,
          USCitizen: false,
          taxId: "",
          holdersMarried: false,
          stepData: {
            step: "0",
            subStep: 1,
            completedSteps: null,
            reachedSteps: null,
            lastCompletedSubStep: 0,
            numericStepList: null,
          },
          formValues: null,
          sendedSteps: [],
          completedForm: false,
        };
          return {
            ...state,
            ...(newHolderData ? newHolderData : emptyHolderData),
            currentHolder: action.payload,
          }
    },
    onSaveCurrentHolder: (state) => {
      const newHolderData: HolderData = {
        currentHolder: state.currentHolder,
        USCitizen: state.USCitizen,
        holdersMarried: state.holdersMarried,
        taxId: state.taxId,
        stepData: state.stepData,
        formValues: state.formValues,
        sendedSteps: state.sendedSteps,
        completedForm: state.completedForm,
      };
      const newHoldersData = state.holdersData.filter(holder => holder.currentHolder !== state.currentHolder);
      newHoldersData.push(newHolderData);
      return {
        ...state,
        holdersData: newHoldersData
      }
    },
    onUpdateDraftUUID: (state, action: PayloadAction<DraftUUID>) => {
      return { ...state, draftUUID: action.payload };
    },
    // Sentry: validation error tracking actions
    onIncrementValidationError: (
      state,
      action: PayloadAction<{ fieldName: string; errorMessage: string }>
    ) => {
      const { fieldName, errorMessage } = action.payload;
      const existingIndex = state?.validationErrorCounts?.findIndex(
        (item) =>
          item.fieldName === fieldName && item.errorMessage === errorMessage
      );

      if (existingIndex !== undefined && existingIndex >= 0) {
        // Increment existing counter
        const newCounts = [...state.validationErrorCounts];
        newCounts[existingIndex] = {
          ...newCounts[existingIndex],
          count: newCounts[existingIndex].count + 1,
        };
        return { ...state, validationErrorCounts: newCounts };
      } else {
        // Add new entry
        return {
          ...state,
          validationErrorCounts: [
            ...(state.validationErrorCounts || []),
            { fieldName, errorMessage, count: 1 },
          ],
        };
      }
    },
    onResetValidationErrorCounts: (state) => {
      return { ...state, validationErrorCounts: [] };
    },
  },
});

export const {
    onUpdateSecuritiesData,
    onResetSecuritiesData,
    onUpdateInitialValidationData,
    onUpdateStep,
    onUpdateSubStep,
    onUpdateCompletedStep,
    onUpdateReachedStep,
    onUpdateLastCompletedSubStep,
    addSendedStep,
    onUpdateFormData,
    onUpdateCompletedForm,
    onUpdateAccountBeneficiaries,
    onUpdateHolder,
    onSaveCurrentHolder,
    onUpdateDraftUUID,
    // Sentry: validation error tracking
    onIncrementValidationError,
    onResetValidationErrorCounts,
} = securitiesSlice.actions;

export default securitiesSlice.reducer;
